import type { Article, ArticleModule } from 'types/capi/article';

import { rendersModule } from '~/utils/moduleImpression';
import { getModulePositions } from '~/utils/modulePositions';
import {
  isMobileViewport,
  isRowModuleHiddenOnMobile
} from '~/utils/moduleVisibility';

export type ArticleModuleManifestEntry = {
  module_position: number;
  module_type: string;
  row_position?: number;
};

export type ArticleViewProperties = {
  article_id: string;
  modules: ArticleModuleManifestEntry[];
};

// The render manifest: every CMS module the article renders with a box, in
// render order, with the same module_position the module impressions carry —
// so the furthest module a reader saw can be read against how many there were.
// A module inside a module-row also carries the row's ordinal as row_position:
// the row's columns sit side by side, so every module in it is the same depth
// and the warehouse ranks them as one slot. Rows are containers and are not
// listed; modules that render nothing are not listed; on a mobile viewport,
// modules a row hides on mobile are not listed, because they are max-md:hidden
// and can never be seen there. All of them still consume an ordinal in
// getModulePositions, so the numbers here are the numbers the impressions
// send. The hardcoded sites around the article (header, related articles,
// recent features) sit in no numbered sequence and are not part of the
// manifest either.
export function getArticleModuleManifest(
  modules: ArticleModule[],
  { mobile = false }: { mobile?: boolean } = {}
): ArticleModuleManifestEntry[] {
  const positions = getModulePositions(modules);
  const manifest: ArticleModuleManifestEntry[] = [];

  const visit = (module: ArticleModule, rowPosition?: number) => {
    if (!rendersModule(module)) return;

    if (module.type === 'module-row') {
      module.columns.forEach((column) =>
        column.modules?.forEach((childModule) => {
          if (mobile && isRowModuleHiddenOnMobile(column, childModule)) return;

          visit(childModule, positions.get(module));
        })
      );

      return;
    }

    const modulePosition = positions.get(module);

    if (modulePosition === undefined) return;

    manifest.push({
      module_position: modulePosition,
      module_type: module.type,
      row_position: rowPosition
    });
  };

  modules.forEach((module) => visit(module));

  return manifest;
}

// A v1 article is prose with no modules, so its manifest is honestly empty
// rather than absent: the contract requires the array from schema_version 2.
// The viewport is read at build time, so the manifest says what this reader's
// screen rendered, the same screen the impressions fire from.
export default function getArticleViewProperties(
  article: Article,
  mobile = isMobileViewport()
): ArticleViewProperties {
  return {
    article_id: article.id,
    modules:
      article.template === 'v2'
        ? getArticleModuleManifest(article.modules, { mobile })
        : []
  };
}
